/**
 * `IN (…)` / `NOT IN (…)` for a list of filter values — the one form both
 * filter emitters write, for the "in list" / "not in list" operators and the
 * `enum` multi-select: the renderer's `filterWhereBuilder.ts` (Visual Query
 * Editor, Data Profile) and the Table Explorer generators' `queryUtils.ts`.
 * Each value is written by the caller's `literal` (the engine's escaper plus
 * the temporal policy), so a list value takes exactly the literal `=` gives
 * the same value. An empty list writes nothing (the filter is inactive).
 *
 * Oracle rejects a list of more than 1,000 expressions (ORA-01795), so there
 * a longer list is split into 1,000-value lists inside one pair of
 * parentheses: `(f IN (…) OR f IN (…))` — the same rows as one list — and
 * `(f NOT IN (…) AND f NOT IN (…))`, which keeps `NOT IN`'s NULL semantics (a
 * NULL `f` is unknown in every part, so the row is left out, as with one
 * list). The other engines take long lists as written (SQL Server, MySQL,
 * PostgreSQL and SQLite have no fixed IN-list limit), so they get one list.
 * `predicateInListBranch.ts` reads both forms back.
 */

import type { DatabaseEngineId } from '../../../../core/engines/engineTypes';

/** Oracle's maximum number of expressions in one IN list (ORA-01795). */
export const ORACLE_IN_LIST_LIMIT = 1000;

export function inListSql(
  quotedField: string,
  values: readonly unknown[],
  literal: (value: unknown) => string,
  engine: DatabaseEngineId | undefined,
  negated = false,
): string {
  if (values.length === 0) return '';
  const keyword = negated ? 'NOT IN' : 'IN';
  const size = engine === 'oracle' ? ORACLE_IN_LIST_LIMIT : values.length;
  const parts: string[] = [];
  for (let start = 0; start < values.length; start += size) {
    parts.push(`${quotedField} ${keyword} (${values.slice(start, start + size).map(literal).join(', ')})`);
  }
  return parts.length === 1 ? parts[0]! : `(${parts.join(negated ? ' AND ' : ' OR ')})`;
}
